import { useState } from 'react';
import { useMutation } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { UPDATE_PROFILE_MUTATION } from '../../graphql/mutations';
import styles from './Profile.module.css';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [companyName, setCompanyName] = useState(user?.companyName ?? '');
  const [sendEmailsTo, setSendEmailsTo] = useState(user?.sendEmailsTo ?? '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [updateProfile, { loading: saving }] = useMutation(UPDATE_PROFILE_MUTATION, {
    onCompleted: (data) => {
      setSaved(true);
      setError('');
      setTimeout(() => setSaved(false), 3000);
      if (user && data?.updateProfile) updateUser({ ...user, ...data.updateProfile });
    },
    onError: (err) => setError(err.message),
  });

  const handleSave = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      return;
    }
    const emailOverride = sendEmailsTo.trim();
    if (emailOverride && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailOverride)) {
      setError('The notification email address is not valid');
      return;
    }
    updateProfile({
      variables: {
        input: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          companyName: companyName.trim(),
          sendEmailsTo: emailOverride,
        },
      },
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Profile</h2>
          <p className="text-on-surface-variant mt-1">Your account details and notification settings.</p>
        </div>

        <div className={styles.card}>
          <h3 className={styles.sectionTitle}>Account</h3>
          <div className={styles.fieldGrid}>
            <div className="space-y-1.5">
              <label htmlFor="profile-first-name" className={styles.label}>First name</label>
              <input id="profile-first-name" className="input-field" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="profile-last-name" className={styles.label}>Last name</label>
              <input id="profile-last-name" className="input-field" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="profile-company" className={styles.label}>Company</label>
              <input id="profile-company" className="input-field" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="profile-email" className={styles.label}>Account email</label>
              <input id="profile-email" className="input-field" value={user?.email ?? ''} disabled />
              <p className={styles.hint}>Used to sign in — cannot be changed here.</p>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <h3 className={styles.sectionTitle}>Notifications</h3>
          <div className="space-y-1.5 max-w-md">
            <label htmlFor="profile-notify-email" className={styles.label}>Send notifications to</label>
            <input
              id="profile-notify-email"
              className="input-field"
              type="email"
              placeholder={user?.email ?? 'name@company.com'}
              value={sendEmailsTo}
              onChange={(e) => setSendEmailsTo(e.target.value)}
            />
            <p className={styles.hint}>
              Emails about your files (e.g. "conversion finished") go to this address.
              Leave empty to receive them at your account email{user?.email ? ` (${user.email})` : ''}.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button onClick={handleSave} disabled={saving} className="btn-primary px-6 py-2.5 rounded-lg text-sm disabled:opacity-50">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {saved && <span className={styles.savedNote}>✓ Saved</span>}
          {error && <span className="text-sm text-error">{error}</span>}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
